import { TMap } from '@shared/types';

export const buildHref = (pathname: string, params: TMap<string, string | undefined> = {}) => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, v);
  }
  const qs = sp.toString();
  return qs ? `${pathname}?${qs}` : pathname;
};

// --- External URL helpers ---

const HTTPS_PREFIX = 'https://';
const HTTP_PREFIX = 'http://';

/** Check if url starts with a known protocol (case-insensitive). */
const hasProtocol = (url: string): boolean => {
  const lower = url.toLowerCase();
  return lower.startsWith(HTTPS_PREFIX) || lower.startsWith(HTTP_PREFIX);
};

/** Prepend `https://` if the URL has no protocol. Does NOT upgrade http → https. */
export const ensureProtocol = (url: string): string => (hasProtocol(url) ? url : `${HTTPS_PREFIX}${url}`);

/** Strip `https://` or `http://` for display. The full URL should still be used in `href`. */
export const stripProtocol = (url: string): string => {
  const lower = url.toLowerCase();
  if (lower.startsWith(HTTPS_PREFIX)) return url.slice(HTTPS_PREFIX.length);
  if (lower.startsWith(HTTP_PREFIX)) return url.slice(HTTP_PREFIX.length);
  return url;
};

/** Returns true if the URL uses http:// (not https://). Used for validation error messages. */
export const isInsecureUrl = (url: string): boolean => url.toLowerCase().startsWith(HTTP_PREFIX);

/**
 * Normalize a URL for storage: trim, ensure protocol, return `undefined` for empty.
 *
 * Return values:
 * - `string`    – a valid, protocol-prefixed URL ready for persistence.
 * - `undefined` – input was empty / whitespace-only (no URL to store).
 *
 * Callers coalesce with `?? null` to get the two-state value (`string | null`)
 * expected by the backend: a URL string to store, or `null` to delete.
 */
export const normalizeUrl = (raw: string | undefined | null): string | undefined => {
  const trimmed = raw?.trim();
  if (!trimmed) return undefined;
  return ensureProtocol(trimmed);
};

/**
 * Yup `.transform()` for URL fields.
 * - Empty / whitespace-only → `undefined` (lets `.notRequired()` skip validation).
 * - Non-empty → trimmed + protocol-ensured (so `.url()` succeeds for bare domains).
 */
export const yupUrlTransform = (v: string | null | undefined): string | undefined => {
  if (!v || !v.trim()) return undefined;
  return ensureProtocol(v.trim());
};
